import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../components/Layout/DashboardLayout";
import { TableSkeletonRows } from "../components/ui/Skeleton";
import { clientsApi } from "../api/clientsApi";
import { disputesApi } from "../api/disputesApi";
import { Client, DisputeItem } from "../types";

const statusStyles: Record<string, string> = {
  active: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  pending: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  review: "bg-blue-500/20 text-blue-300 border-blue-500/40",
};

function scorePillClass(score: number) {
  if (score < 580) return "bg-red-500/20 text-red-300 border-red-500/40";
  if (score < 670) return "bg-amber-500/20 text-amber-300 border-amber-500/40";
  return "bg-emerald-500/20 text-emerald-300 border-emerald-500/40";
}

const ACTIVE_DISPUTE_STATUSES = new Set(["indispute", "pending", "review", "active"]);

/** Matches ClientDetailPage: 0% if no disputes; else resolved/total clamped 5–100%. */
function disputeResolutionProgressPercent(disputes: DisputeItem[]): number {
  if (disputes.length === 0) return 0;
  const resolved = disputes.filter((d) => d.status === "Deleted/Resolved").length;
  const pct = Math.round((resolved / disputes.length) * 100);
  return Math.min(100, Math.max(5, pct));
}

const ClientsPage: React.FC = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [disputeStatsByClient, setDisputeStatsByClient] = useState<
    Record<string, { activeCount: number; progressPercent: number } | null>
  >({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  useEffect(() => {
    const loadClients = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await clientsApi.getAll();
        setClients(data);

        const disputeResults = await Promise.allSettled(
          data.map(async (client) => {
            const disputes = await disputesApi.getAllForClient(client.id);
            const activeCount = disputes.filter((d) => ACTIVE_DISPUTE_STATUSES.has(d.status.toLowerCase())).length;
            const progressPercent = disputeResolutionProgressPercent(disputes);
            return { clientId: client.id, activeCount, progressPercent };
          })
        );

        const next: Record<string, { activeCount: number; progressPercent: number } | null> = {};
        disputeResults.forEach((result, index) => {
          const id = data[index].id;
          if (result.status === "fulfilled") {
            next[id] = {
              activeCount: result.value.activeCount,
              progressPercent: result.value.progressPercent,
            };
          } else {
            next[id] = null;
          }
        });
        setDisputeStatsByClient(next);
      } catch {
        setError("Failed to load clients. Please refresh and try again.");
      } finally {
        setLoading(false);
      }
    };

    void loadClients();
  }, []);

  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => {
      const fullName = `${c.firstName} ${c.lastName}`.toLowerCase();
      return fullName.includes(q) || c.email.toLowerCase().includes(q);
    });
  }, [clients, search]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <div>
            <h1 className="text-2xl font-semibold text-white">Clients</h1>
            <p className="text-sm text-slate-400">Manage all client profiles and progress.</p>
          </div>
          <button
            onClick={() => navigate("/clients/add")}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500"
          >
            Add Client
          </button>
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 outline-none ring-blue-500 placeholder:text-slate-500 focus:ring-2"
          />
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
          {loading && (
            <div className="space-y-3 py-2" aria-busy="true" aria-label="Loading clients">
              <TableSkeletonRows rows={9} cols={4} />
            </div>
          )}
          {!loading && error && <p className="text-sm text-red-300">{error}</p>}

          {!loading && !error && filteredClients.length === 0 && (
            <div className="py-14 text-center">
              <p className="text-lg text-slate-200">No clients found.</p>
              <p className="mt-1 text-sm text-slate-400">Create your first client to get started.</p>
            </div>
          )}

          {!loading && !error && filteredClients.length > 0 && (
            <>
              <div className="space-y-3 md:hidden">
                {filteredClients.map((client) => {
                  const statusKey = client.status.toLowerCase();
                  const stats = disputeStatsByClient[client.id];
                  const name = `${client.firstName} ${client.lastName}`;
                  return (
                    <div
                      key={client.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate(`/clients/${client.id}`)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          navigate(`/clients/${client.id}`);
                        }
                      }}
                      className="cursor-pointer rounded-lg border border-slate-800 bg-slate-800/30 p-4 transition hover:bg-slate-800/50"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium text-white">{name}</p>
                          <p className="mt-1 break-all text-sm text-slate-400">{client.email}</p>
                          <p className="mt-0.5 text-sm text-slate-400">{client.phone || "—"}</p>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/clients/${client.id}`);
                          }}
                          className="shrink-0 rounded-md border border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-200 hover:bg-slate-800"
                        >
                          View
                        </button>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {[client.equifaxScore, client.experianScore, client.transUnionScore].map((score, idx) => (
                          <span
                            key={`${client.id}-m-${idx}`}
                            className={`rounded-full border px-2 py-1 text-xs ${scorePillClass(score)}`}
                          >
                            {score}
                          </span>
                        ))}
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <span
                          className={`rounded-full border px-2 py-1 text-xs ${
                            statusStyles[statusKey] ?? "border-slate-700 bg-slate-800 text-slate-300"
                          }`}
                        >
                          {client.status}
                        </span>
                        <span className="text-xs text-slate-400">
                          {stats == null ? "—" : `${stats.activeCount} active dispute${stats.activeCount === 1 ? "" : "s"}`}
                        </span>
                      </div>
                      <div className="mt-3">
                        {stats == null ? (
                          <p className="text-xs text-slate-400">Progress —</p>
                        ) : (
                          <>
                            <div className="h-2 overflow-hidden rounded-full bg-slate-800">
                              <div
                                className="h-full bg-blue-500"
                                style={{ width: `${stats.progressPercent}%` }}
                              />
                            </div>
                            <p className="mt-1 text-xs text-slate-400">Progress {stats.progressPercent}%</p>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="hidden overflow-x-auto md:block">
                <table className="min-w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-800 text-slate-400">
                      <th className="pb-3">Name</th>
                      <th className="pb-3">Email</th>
                      <th className="pb-3">Phone</th>
                      <th className="pb-3">Scores (EQ/EX/TU)</th>
                      <th className="pb-3">Status</th>
                      <th className="pb-3">Active Disputes</th>
                      <th className="pb-3">Progress</th>
                      <th className="pb-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClients.map((client) => {
                      const statusKey = client.status.toLowerCase();
                      const stats = disputeStatsByClient[client.id];
                      return (
                        <tr
                          key={client.id}
                          onClick={() => navigate(`/clients/${client.id}`)}
                          className="cursor-pointer border-b border-slate-800/60 text-slate-200 hover:bg-slate-800/50"
                        >
                          <td className="py-3 font-medium">{`${client.firstName} ${client.lastName}`}</td>
                          <td className="py-3 text-slate-300">{client.email}</td>
                          <td className="py-3 text-slate-300">{client.phone || "-"}</td>
                          <td className="py-3">
                            <div className="flex gap-2">
                              {[client.equifaxScore, client.experianScore, client.transUnionScore].map((score, idx) => (
                                <span
                                  key={`${client.id}-${idx}`}
                                  className={`rounded-full border px-2 py-1 text-xs ${scorePillClass(score)}`}
                                >
                                  {score}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="py-3">
                            <span
                              className={`rounded-full border px-2 py-1 text-xs ${
                                statusStyles[statusKey] ?? "border-slate-700 bg-slate-800 text-slate-300"
                              }`}
                            >
                              {client.status}
                            </span>
                          </td>
                          <td className="py-3">{stats == null ? "—" : stats.activeCount}</td>
                          <td className="py-3">
                            <div className="w-28">
                              {stats == null ? (
                                <p className="text-xs text-slate-400">—</p>
                              ) : (
                                <>
                                  <div className="h-2 overflow-hidden rounded-full bg-slate-800">
                                    <div
                                      className="h-full bg-blue-500"
                                      style={{ width: `${stats.progressPercent}%` }}
                                    />
                                  </div>
                                  <p className="mt-1 text-xs text-slate-400">{stats.progressPercent}%</p>
                                </>
                              )}
                            </div>
                          </td>
                          <td className="py-3">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/clients/${client.id}`);
                              }}
                              className="rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ClientsPage;
